/**
 * brioCAST LINE Notification Preferences Page
 * Drop-in for existing CRA portal
 *
 * Add to renderPage():
 *   case "notifications": return <NotificationsPage account={account} />;
 * Add to Sidebar:
 *   { key: "notifications", label: "通知設定", icon: "🔔" }
 */
import { useState, useEffect } from "react";

const API_V1 = "https://api.briolabs.io/api/v1";
async function apiV1(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers };
  const res = await fetch(`${API_V1}${path}`, { ...options, headers, body: options.body ? JSON.stringify(options.body) : undefined });
  return res.json();
}

const t = {
  card: "#1A1D27", border: "#2A2E3A", accent: "#6C5CE7", accentLight: "#A29BFE",
  success: "#00B894", danger: "#E17055", warning: "#FDCB6E",
  text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
};

const PREF_ITEMS = [
  { key: "device_alerts", label: "設備上下線通知", desc: "螢幕上線/離線時推播提醒", icon: "📺" },
  { key: "payout_notifications", label: "分潤通知", desc: "每月分潤結算完成時通知", icon: "💰" },
  { key: "daily_digest", label: "每日營收摘要", desc: "每晚 10 點推播今日營收統計", icon: "📊" },
  { key: "campaign_updates", label: "廣告狀態更新", desc: "廣告審核通過/到期通知", icon: "📢" },
  { key: "emergency", label: "緊急通知", desc: "政府示警、系統異常等重要通知", icon: "🚨", locked: true },
];

export default function NotificationsPage({ account }) {
  const [prefs, setPrefs] = useState(null);
  const [bound, setBound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testSent, setTestSent] = useState(false);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    Promise.all([
      apiV1("/notifications/preferences"),
      apiV1("/notifications/stats?days=30"),
    ]).then(([prefsRes, statsRes]) => {
      setBound(prefsRes.data?.bound || false);
      setPrefs(prefsRes.data?.preferences || null);
      setStats(statsRes.data?.stats || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const togglePref = async (key) => {
    if (key === "emergency") return;
    const newVal = !prefs[key];
    const updated = { ...prefs, [key]: newVal };
    setPrefs(updated);
    try { await apiV1("/notifications/preferences", { method: "PUT", body: { [key]: newVal } }); }
    catch { setPrefs({ ...prefs }); }
  };

  const sendTest = async () => {
    try { await apiV1("/notifications/test", { method: "POST" }); setTestSent(true); setTimeout(() => setTestSent(false), 3000); } catch {}
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: t.textMuted }}>載入中...</div>;

  const totalSent = stats.filter(s => s.status === "sent").reduce((a, b) => a + parseInt(b.count), 0);
  const totalFailed = stats.filter(s => s.status === "failed").reduce((a, b) => a + parseInt(b.count), 0);

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>🔔 通知設定</h2>
      <p style={{ color: t.textMuted, fontSize: 14, marginBottom: 24 }}>管理 LINE 推播通知偏好</p>

      {/* Binding status */}
      <div style={{
        background: bound ? `${t.success}12` : `${t.warning}12`,
        border: `1px solid ${bound ? `${t.success}30` : `${t.warning}30`}`,
        borderRadius: 12, padding: "16px 20px", marginBottom: 20,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>{bound ? "✅" : "⚠️"}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{bound ? "LINE 已綁定" : "LINE 尚未綁定"}</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>{bound ? "通知將透過 LINE OA 推播" : "請至 LINE 官方帳號輸入綁定碼以啟用通知"}</div>
          </div>
        </div>
        {bound && (
          <button onClick={sendTest} disabled={testSent} style={{
            padding: "8px 16px", borderRadius: 8, border: `1px solid ${testSent ? `${t.success}40` : t.border}`,
            background: testSent ? `${t.success}15` : "transparent", color: testSent ? t.success : t.text,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
            {testSent ? "✓ 已發送" : "🔔 發送測試"}
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 16, alignItems: "start" }}>
        {/* Preferences */}
        <div style={{ background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border}`, fontSize: 15, fontWeight: 700 }}>通知類型</div>
          {PREF_ITEMS.map((item, idx) => {
            const enabled = prefs ? prefs[item.key] !== false : true;
            return (
              <div key={item.key} style={{
                display: "flex", alignItems: "center", gap: 14, padding: "14px 20px",
                borderBottom: idx < PREF_ITEMS.length - 1 ? `1px solid rgba(255,255,255,0.04)` : "none",
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: enabled ? `${t.accent}15` : "rgba(255,255,255,0.04)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{item.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: t.textMuted, marginTop: 1 }}>{item.desc}</div>
                </div>
                {item.locked ? (
                  <span style={{ fontSize: 11, color: t.success, fontWeight: 600 }}>永遠開啟</span>
                ) : (
                  <div onClick={() => bound && togglePref(item.key)} style={{
                    width: 44, height: 24, borderRadius: 24, position: "relative", cursor: bound ? "pointer" : "not-allowed",
                    background: enabled ? t.accent : t.border, transition: "0.3s", opacity: bound ? 1 : 0.4,
                  }}>
                    <div style={{ position: "absolute", top: 3, left: enabled ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "0.3s" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div style={{ background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>📈 過去 30 天</div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, background: `${t.success}10`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: t.success }}>{totalSent}</div>
              <div style={{ fontSize: 11, color: t.textMuted }}>已發送</div>
            </div>
            <div style={{ flex: 1, background: `${t.danger}10`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: totalFailed > 0 ? t.danger : t.textDim }}>{totalFailed}</div>
              <div style={{ fontSize: 11, color: t.textMuted }}>失敗</div>
            </div>
          </div>
          {stats.filter(s => s.status === "sent").map(s => (
            <div key={s.notification_type} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
              <span style={{ fontSize: 12, color: t.textMuted }}>{s.notification_type}</span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{s.count}</span>
            </div>
          ))}
          {stats.length === 0 && <div style={{ fontSize: 12, color: t.textDim, textAlign: "center", padding: 10 }}>尚無通知記錄</div>}
        </div>
      </div>
    </div>
  );
}
